import React, { useMemo, useState } from 'react';
import { firestore } from './firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import jsPDF from 'jspdf';
import './Monatsauszug.css';

const Monatsauszug = () => {
  const months = useMemo(() => {
    const arr = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      arr.push({
        label: d.toLocaleString('de-DE', { month: 'long', year: 'numeric' }),
        month: d.getMonth(),
        year: d.getFullYear(),
        value: `${d.getFullYear()}-${d.getMonth()}`,
      });
    }
    return arr;
  }, []);

  const [selected, setSelected] = useState(months[0].value);

  const getWeekNumber = (date) => {
    const start = new Date(date.getFullYear(), 0, 1);
    const diff = (date - start) / 86400000;
    return Math.ceil((diff + start.getDay() + 1) / 7);
  };

  const handleDownload = async () => {
    const { month, year, label } = months.find((m) => m.value === selected);
    try {
      const usersSnap = await getDocs(collection(firestore, 'users'));
      const userCompany = {};
      const userNames = {};
      usersSnap.forEach((u) => {
        const data = u.data();
        userCompany[u.id] = data.Company || 'Unbekannt';
        if (data.Name) userNames[u.id] = data.Name;
      });

      const counts = {};
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const dayMap = { 1: 'Montag', 2: 'Dienstag', 3: 'Mittwoch', 4: 'Donnerstag', 5: 'Freitag' };

      for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
        const dayIdx = d.getDay();
        if (!dayMap[dayIdx]) continue;
        const week = getWeekNumber(d);
        const path = `Week-${week}-${d.getFullYear()}`;
        const snap = await getDoc(doc(firestore, path, dayMap[dayIdx]));
        if (!snap.exists()) continue;
        const data = snap.data();
        Object.keys(data).forEach((user) => {
          const comp = userCompany[user] || 'Unbekannt';
          if (!counts[comp]) {
            counts[comp] = { total: 0, users: {} };
          }
          counts[comp].total += 1;
          counts[comp].users[user] = (counts[comp].users[user] || 0) + 1;
        });
      }

      if (Object.keys(counts).length === 0) {
        alert('Keine Bestellungen für diesen Monat.');
        return;
      }

      const pdf = new jsPDF();
      
      // Title section with background
      pdf.setFillColor(80, 80, 80);
      pdf.rect(10, 10, 190, 10, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.text(`Mittagsessen Monatsauszug`, 15, 17);
      pdf.setTextColor(0, 0, 0);
      
      // Month as clear subheading
      pdf.setFillColor(200, 200, 200);
      pdf.rect(10, 22, 190, 8, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      pdf.text(label, 15, 28);

      let y = 35;
      pdf.setFontSize(11);
      
      // Summary by company
      Object.entries(counts)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .forEach(([comp, info], idx) => {
          // Alternating row colors
          if (idx % 2 === 0) {
            pdf.setFillColor(240, 240, 240);
            pdf.rect(10, y - 4, 190, 6 + (Object.keys(info.users).length * 6), 'F');
          }
          
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${comp}`, 12, y);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${info.total}`, 170, y);
          y += 7;
          
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          Object.entries(info.users)
            .sort((a, b) =>
              (userNames[a[0]] || a[0]).localeCompare(userNames[b[0]] || b[0])
            )
            .forEach(([user, cnt]) => {
              const display = userNames[user] || user;
              pdf.text(`${display}`, 20, y);
              pdf.text(`${cnt}`, 170, y);
              y += 5;
              if (y > 270) {
                pdf.addPage();
                y = 20;
              }
            });
          y += 3;
          pdf.setFontSize(11);
          if (y > 270) {
            pdf.addPage();
            y = 20;
          }
        });
      
      // Total section
      const total = Object.values(counts).reduce((sum, info) => sum + info.total, 0);
      y += 2;
      pdf.setFillColor(80, 80, 80);
      pdf.rect(10, y - 5, 190, 10, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text(`Gesamtbestellungen: ${total}`, 15, y + 1);
      pdf.setTextColor(0, 0, 0);

      // Add page break and detailed section
      pdf.addPage();
      y = 10;
      
      // Detailed section title with background
      pdf.setFillColor(80, 80, 80);
      pdf.rect(10, 10, 190, 10, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.text(`Detaillierte Bestellungen`, 15, 17);
      pdf.setTextColor(0, 0, 0);
      
      // Month as clear subheading
      pdf.setFillColor(200, 200, 200);
      pdf.rect(10, 22, 190, 8, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      pdf.text(label, 15, 28);
      y = 35;

      // Build detailed day-by-day report
      const dailyOrders = {};
      for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
        const dayIdx = d.getDay();
        if (!dayMap[dayIdx]) continue;
        const week = getWeekNumber(d);
        const path = `Week-${week}-${d.getFullYear()}`;
        const snap = await getDoc(doc(firestore, path, dayMap[dayIdx]));
        if (!snap.exists()) continue;
        
        const data = snap.data();
        if (Object.keys(data).length === 0) continue;
        
        const dateStr = d.toLocaleString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        dailyOrders[dateStr] = {};
        Object.entries(data).forEach(([user, order]) => {
          dailyOrders[dateStr][user] = order;
        });
      }

      // Display daily orders
      Object.entries(dailyOrders).forEach(([dateStr, orders]) => {
        const dayCount = Object.keys(orders).length;
        
        // Day header with light gray background
        pdf.setFillColor(180, 180, 180);
        pdf.rect(10, y - 4, 190, 7, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.text(`${dateStr}`, 12, y);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.text(`${dayCount} Bestellung${dayCount !== 1 ? 'en' : ''}`, 140, y);
        y += 8;
        
        // Day entries
        pdf.setFontSize(9);
        Object.entries(orders)
          .sort((a, b) =>
            (userNames[a[0]] || a[0]).localeCompare(userNames[b[0]] || b[0])
          )
          .forEach(([user, order], idx) => {
            // Alternating row colors for entries
            if (idx % 2 === 0) {
              pdf.setFillColor(240, 240, 240);
              pdf.rect(10, y - 3.5, 190, 5, 'F');
            }
            
            const display = userNames[user] || user;
            const orderStr = typeof order === 'object' ? JSON.stringify(order) : order;
            pdf.text(`${display}`, 15, y);
            pdf.setFont('helvetica', 'italic');
            pdf.text(`${orderStr}`, 70, y);
            pdf.setFont('helvetica', 'normal');
            y += 5;
            if (y > 270) {
              pdf.addPage();
              y = 20;
            }
          });
        y += 4;
        if (y > 270) {
          pdf.addPage();
          y = 20;
        }
      });

      const fileMonth = String(month + 1).padStart(2, '0');
      pdf.save(`Mittagsessen-Monatsauszug-${year}-${fileMonth}.pdf`);
    } catch (err) {
      console.error(err);
      alert('Fehler beim Generieren des PDFs');
    }
  };

  return (
    <div className="monatsauszug-container fade-in">
      <h2>Monatsauszug</h2>
      <select value={selected} onChange={(e) => setSelected(e.target.value)}>
        {months.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>
      <button onClick={handleDownload}>Herunterladen</button>
    </div>
  );
};

export default Monatsauszug;